import React from 'react';
import { CaretRight } from '@phosphor-icons/react';
import { Link } from '../../Link';

export type MenuRowNoteTone = 'muted' | 'news';

export interface MenuRowProps {
  label: React.ReactNode;
  /** Right-aligned annotation: a live figure ("4 saved · 2 active") or what the page holds ("jobs by city"). */
  note?: React.ReactNode;
  /** `news` renders the note in gold, the app's existing "something changed" colour (see HubTabs). */
  noteTone?: MenuRowNoteTone;
  /** Logical path handed to <Link>, which localizes it for `locale`. */
  href?: string;
  locale?: string;
  /** Render a plain <a> instead of <Link>: for routes that are deliberately not locale-prefixed. */
  plainAnchor?: boolean;
  onClick?: () => void;
  /** Replaces the trailing caret (e.g. a switch). */
  trailing?: React.ReactNode;
  /**
   * A live figure instead of a note: the number set at the row's own size in the display face,
   * the unit word small after it, so the count is the first thing the eye lands on.
   */
  figure?: { value: string; unit: React.ReactNode };
  /** Shorter row for nested lists (the admin disclosure). */
  compact?: boolean;
  /** At lg+ the row becomes a bordered tile (label above note), for the two-column Explore grid. */
  tile?: boolean;
  /** Tile that spans the grid and sets its figure larger: the one hero cell of the grid. */
  hero?: boolean;
  'aria-expanded'?: boolean;
}

const ROW_CLASS =
  'group grid w-[calc(100%+1.25rem)] grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 px-2.5 -mx-2.5 rounded-xl text-left ' +
  'transition-colors duration-(--jc-duration-fast) hover:bg-jc-elevated/70 focus-visible:outline-none focus-visible:ring-2 ' +
  'focus-visible:ring-jc-focus focus-visible:ring-inset cursor-pointer';

const LABEL_CLASS = 'font-display font-semibold text-lg leading-tight tracking-[-0.01em] text-jc-ink lg:text-base truncate';

const TILE_CLASS =
  'lg:grid-cols-1 lg:auto-rows-min lg:items-start lg:gap-y-1.5 lg:mx-0 lg:w-auto lg:min-h-0 lg:px-3.5 lg:py-3 ' +
  'lg:border lg:border-jc-border lg:rounded-xl lg:bg-jc-surface lg:hover:bg-jc-surface lg:hover:border-jc-accent/50';
const HERO_CLASS = 'lg:col-span-2 lg:py-3.5';

const NOTE_CLASS: Record<MenuRowNoteTone, string> = {
  muted: 'text-jc-ink-secondary',
  news: 'text-jc-ink font-semibold',
};

/**
 * One line of the menu's index: destination name on the left, annotation and caret on the right.
 * No icon tiles: the annotation column carries the meaning the icons used to gesture at.
 */
export const MenuRow: React.FC<MenuRowProps> = ({
  label,
  note,
  noteTone = 'muted',
  href,
  locale,
  plainAnchor = false,
  onClick,
  trailing,
  figure,
  compact = false,
  tile = false,
  hero = false,
  'aria-expanded': ariaExpanded,
}) => {
  const className = `${ROW_CLASS} ${compact ? 'min-h-9' : 'min-h-[42px] lg:min-h-11'} ${tile ? TILE_CLASS : ''} ${tile && hero ? HERO_CLASS : ''}`;
  const content = (
    <>
      <span className={`${LABEL_CLASS} ${tile && hero ? 'lg:text-lg' : ''}`}>{label}</span>
      <span className={`flex items-center gap-2 justify-end ${tile ? 'lg:justify-start' : ''}`}>
        {figure ? (
          <span className="flex items-baseline gap-1 whitespace-nowrap">
            <span className={`font-display font-semibold tabular-nums leading-none text-jc-ink text-lg lg:text-base ${tile && hero ? 'lg:text-xl' : ''}`}>
              {figure.value}
            </span>
            <span className="text-xs leading-none text-jc-ink-secondary">{figure.unit}</span>
          </span>
        ) : note != null && note !== '' ? (
          <span className={`text-xs leading-tight tabular-nums text-right whitespace-nowrap ${NOTE_CLASS[noteTone]} ${tile ? 'lg:text-left' : ''}`}>{note}</span>
        ) : null}
        {trailing ?? (
          <CaretRight
            className={`w-3.5 h-3.5 shrink-0 text-jc-ink-secondary transition-transform duration-(--jc-duration-fast) group-hover:translate-x-0.5 motion-reduce:transition-none ${tile ? 'lg:hidden' : ''}`}
            aria-hidden
          />
        )}
      </span>
    </>
  );

  if (href && plainAnchor) {
    return (
      <a href={href} onClick={onClick} className={className}>
        {content}
      </a>
    );
  }
  if (href) {
    return (
      <Link href={href} locale={locale} onClick={onClick} className={className}>
        {content}
      </Link>
    );
  }
  return (
    <button type="button" onClick={onClick} aria-expanded={ariaExpanded} className={className}>
      {content}
    </button>
  );
};
